// src/pages/admin/ComponentsPane.js
import { useEffect, useState } from "react";
import { db } from "../../firebaseConfig";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import ToggleSwitch from "./ToggleSwitch";

const cx = (...xs) => xs.filter(Boolean).join(" ");

function ComponentsPane() {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const snap = await getDocs(collection(db, "benefit_components"));
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        setComponents(list);
      } catch (err) {
        console.error("Components load error:", err);
        setError(err.message || "Failed to load components.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleActive = async (comp) => {
    try {
      const ref = doc(db, "benefit_components", comp.id);
      const next = { ...comp, active: comp.active === false ? true : false };
      delete next.id;
      await setDoc(ref, next, { merge: true });
      setComponents((prev) =>
        prev.map((c) =>
          c.id === comp.id ? { ...c, active: next.active } : c
        )
      );
    } catch (err) {
      console.error("Component toggle error:", err);
      alert("Error updating component: " + err.message);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-slate-900">
            Farmer Benefit Components
          </h2>
          <p className="text-xs md:text-sm text-slate-500 mt-0.5">
            Control which components are visible to farmers and dealers –
            Planting Material, Machinery, Drip / Talavdi, INM/IPM, GAP/Soil
            Testing, Precision Tech, Innovative Tools, etc.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-3 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">
          ⚠ Firestore error while loading{" "}
          <b>benefit_components</b>:{" "}
          <span className="font-mono">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <div className="text-sm text-slate-500">Loading components…</div>
        ) : components.length === 0 ? (
          <div className="text-sm text-slate-500">
            No components configured yet. Create documents in{" "}
            <code>benefit_components</code> collection with fields like{" "}
            <code>name</code>, <code>description</code> and{" "}
            <code>active</code>.
          </div>
        ) : (
          components.map((c) => (
            <div
              key={c.id}
              className="bg-white border border-slate-200 rounded-xl p-4 flex items-start justify-between shadow-sm"
            >
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  {c.name || c.id}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 max-w-xs">
                  {c.description ||
                    "Component available for farmer benefit schemes and dealer product mapping."}
                </div>
                <div className="mt-2 text-[11px] text-slate-400">
                  ID: <span className="font-mono">{c.id}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <ToggleSwitch
                  checked={c.active !== false}
                  onChange={() => toggleActive(c)}
                />
                <span className="text-[11px] mt-1 text-slate-500">
                  {c.active === false
                    ? "Hidden from portals"
                    : "Visible in portals"}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ComponentsPane;
